import React from 'react';
import ReactECharts from 'echarts-for-react';
import { PG } from '../../utils/theme';

const baseGrid = { top: 30, right: 16, bottom: 28, left: 42, containLabel: true };

const mono = 'Space Mono, monospace';

export const PGLineChart = ({ categories, series, height = '100%', yFormatter }) => {
  const option = {
    backgroundColor: 'transparent',
    grid: baseGrid,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#000',
      borderColor: PG.red,
      borderWidth: 1,
      textStyle: { color: '#fff', fontFamily: mono, fontSize: 11 },
    },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: PG.neutral400, fontFamily: mono, fontSize: 10 },
      itemWidth: 10,
      itemHeight: 2,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: PG.borderMid } },
      axisTick: { show: false },
      axisLabel: { color: PG.neutral500, fontFamily: mono, fontSize: 10 },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: PG.neutral500,
        fontFamily: mono,
        fontSize: 10,
        formatter: yFormatter,
      },
      splitLine: { lineStyle: { color: PG.borderDim, type: 'dashed' } },
    },
    series: series.map((s, i) => ({
      type: 'line',
      smooth: false,
      showSymbol: false,
      lineStyle: { width: 2 },
      areaStyle: i === 0 ? { color: PG.red, opacity: 0.08 } : undefined,
      ...s,
    })),
  };
  return <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'svg' }} />;
};

export const PGBarChart = ({ categories, series, height = '100%', horizontal = false, yFormatter }) => {
  const catAxis = {
    type: 'category',
    data: categories,
    axisLine: { lineStyle: { color: PG.borderMid } },
    axisTick: { show: false },
    axisLabel: { color: PG.neutral500, fontFamily: mono, fontSize: 10 },
  };
  const valAxis = {
    type: 'value',
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: PG.neutral500, fontFamily: mono, fontSize: 10, formatter: yFormatter },
    splitLine: { lineStyle: { color: PG.borderDim, type: 'dashed' } },
  };
  const option = {
    backgroundColor: 'transparent',
    grid: baseGrid,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#000',
      borderColor: PG.red,
      borderWidth: 1,
      textStyle: { color: '#fff', fontFamily: mono, fontSize: 11 },
    },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: PG.neutral400, fontFamily: mono, fontSize: 10 },
      itemWidth: 10,
      itemHeight: 10,
    },
    xAxis: horizontal ? valAxis : catAxis,
    yAxis: horizontal ? catAxis : valAxis,
    series: series.map((s) => ({
      type: 'bar',
      barMaxWidth: 18,
      itemStyle: { borderRadius: horizontal ? [0, 2, 2, 0] : [2, 2, 0, 0] },
      ...s,
    })),
  };
  return <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'svg' }} />;
};

export const PGGauge = ({ value, min = 0, max = 100, label, suffix = '%' }) => {
  const color =
    value >= 95 ? PG.emerald : value >= 85 ? PG.amber : PG.red;
  const option = {
    backgroundColor: 'transparent',
    series: [
      {
        type: 'gauge',
        min,
        max,
        startAngle: 210,
        endAngle: -30,
        progress: { show: true, width: 14, itemStyle: { color } },
        axisLine: { lineStyle: { width: 14, color: [[1, PG.borderMid]] } },
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title: {
          offsetCenter: [0, '78%'],
          color: PG.neutral500,
          fontFamily: mono,
          fontSize: 10,
        },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, '-5%'],
          formatter: `{value}${suffix}`,
          color: '#fff',
          fontFamily: 'Doto, sans-serif',
          fontSize: 42,
          fontWeight: 900,
        },
        data: [{ value, name: label || '' }],
      },
    ],
  };
  return <ReactECharts option={option} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />;
};

export const PGPieChart = ({ data, height = '100%' }) => {
  const palette = [PG.red, PG.amber, PG.emerald, PG.cyan, PG.violet, PG.white, PG.neutral500];
  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: '#000',
      borderColor: PG.red,
      borderWidth: 1,
      textStyle: { color: '#fff', fontFamily: mono, fontSize: 11 },
    },
    legend: {
      orient: 'vertical',
      right: 0,
      top: 'center',
      textStyle: { color: PG.neutral400, fontFamily: mono, fontSize: 10 },
      itemWidth: 10,
      itemHeight: 10,
    },
    series: [
      {
        type: 'pie',
        radius: ['55%', '78%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: PG.black, borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: data.map((d, i) => ({ ...d, itemStyle: { color: d.color || palette[i % palette.length] } })),
      },
    ],
  };
  return <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'svg' }} />;
};
